"use client";

import React, { useState } from "react";
import Reveal from "@/components/common/Reveal";
import { ShieldAlert, DollarSign, Layers, ArrowRight, CircleCheck, FileText, CircleHelp } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface LocalizedField {
  en: string;
  zh: string;
  es: string;
  de: string;
  fr: string;
  pt: string;
}
interface AlloyGrade {
  grade: string;
  category: LocalizedField;
  composition: string;
  corrosionRating: LocalizedField;
  costFactor: LocalizedField;
  recommendedUse: LocalizedField;
  weldability: LocalizedField;
  description: LocalizedField;
}
const alloyData: AlloyGrade[] = [{
  grade: "AISI 304",
  category: {
    en: "Austenitic Standard",
    zh: "标准奥氏体不锈钢",
    es: "Austenítico estándar",
    de: "Austenitischer Standard",
    fr: "Austénitique standard",
    pt: "Austenítico padrão"
  },
  composition: "18% Cr / 8% Ni",
  corrosionRating: {
    en: "Good (Atmospheric & Fresh Water)",
    zh: "良好（大气与淡水环境）",
    es: "Buena (atmosférica y agua dulce)",
    de: "Gut (Atmosphäre & Süßwasser)",
    fr: "Bonne (atmosphérique et eau douce)",
    pt: "Boa (atmosférica e água doce)"
  },
  costFactor: {
    en: "1.0x (Baseline)",
    zh: "1.0x（基准成本）",
    es: "1.0x (Línea base)",
    de: "1.0x (Basis)",
    fr: "1.0x (Base de référence)",
    pt: "1.0x (Referência)"
  },
  recommendedUse: {
    en: "Food processing, general screening, architectural, basic liquid filtration",
    zh: "食品加工、通用筛分、建筑装饰、基础液体过滤",
    es: "Procesamiento de alimentos, tamizado general, arquitectura, filtración básica de líquidos",
    de: "Lebensmittelverarbeitung, allgemeine Siebung, Architektur, Basis-Flüssigkeitsfiltration",
    fr: "Agroalimentaire, criblage général, architecture, filtration liquide standard",
    pt: "Processamento de alimentos, peneiramento geral, arquitetura, filtração básica de líquidos"
  },
  weldability: {
    en: "Standard (Risk of carbide precipitation)",
    zh: "标准（焊接存在晶间碳化物析出风险）",
    es: "Estándar (riesgo de precipitación de carburos)",
    de: "Standard (Risiko von Karbidausscheidungen)",
    fr: "Standard (risque de précipitation de carbures)",
    pt: "Padrão (risco de precipitação de carbetos)"
  },
  description: {
    en: "The most versatile and widely specified stainless steel mesh grade for standard commercial and non-critical industrial screening.",
    zh: "应用最广泛的通用不锈钢丝网牌号，适用于常规商业过滤与非严苛工业筛分环境。",
    es: "El grado de malla de acero inoxidable más versátil y utilizado para tamizado comercial estándar e industrial no crítico.",
    de: "Die vielseitigste und am häufigsten spezifizierte Edelstahl-Gewebegüte für gewerbliche und unkritische industrielle Siebaufgaben.",
    fr: "La nuance de toile inox la plus polyvalente et la plus répandue pour le criblage commercial courant et industriel non critique.",
    pt: "O grau de tela de aço inoxidável mais versátil e especificado para peneiramento comercial padrão e industrial não crítico."
  }
}, {
  grade: "AISI 304L",
  category: {
    en: "Low-Carbon Austenitic",
    zh: "超低碳奥氏体不锈钢",
    es: "Austenítico bajo en carbono",
    de: "Kohlenstoffarmer Austenit",
    fr: "Austénitique bas carbone",
    pt: "Austenítico de baixo carbono"
  },
  composition: "18% Cr / 8% Ni / <0.03% C",
  corrosionRating: {
    en: "Good (Intergranular Resistance)",
    zh: "优良（抗晶间腐蚀）",
    es: "Buena (resistencia intergranular)",
    de: "Gut (interkristalline Beständigkeit)",
    fr: "Bonne (résistance intergranulaire)",
    pt: "Boa (resistência intergranular)"
  },
  costFactor: {
    en: "1.1x",
    zh: "1.1x",
    es: "1.1x",
    de: "1.1x",
    fr: "1.1x",
    pt: "1.1x"
  },
  recommendedUse: {
    en: "Welded filter cylinders, sintered multi-layer packs, chemical filter housings",
    zh: "焊接滤筒、多层烧结滤网组合包、化工滤器外壳组件",
    es: "Cilindros de filtro soldados, paquetes multicapa sinterizados, carcasas de filtros químicos",
    de: "Geschweißte Filterzylinder, mehrlagige Sinterfilterelemente, Chemie-Filtergehäuse",
    fr: "Cylindres filtrants soudés, packs multicouches frittés, corps de filtres chimiques",
    pt: "Cilindros filtrantes soldados, elementos sinterizados multicamadas, carcaças de filtros químicos"
  },
  weldability: {
    en: "Excellent (No post-weld annealing needed)",
    zh: "优异（焊后无需退火处理）",
    es: "Excelente (no requiere recocido post-soldadura)",
    de: "Hervorragend (kein Lösungsglühen nach dem Schweißen nötig)",
    fr: "Excellente (aucun recuit après soudage requis)",
    pt: "Excelente (dispensa recozimento pós-soldagem)"
  },
  description: {
    en: "Low carbon variation of 304 formulated to eliminate carbide precipitation during welding and sintering operations.",
    zh: "304的超低碳衍生牌号，专门消除高温焊接及真空烧结过程中的晶间碳化物析出。",
    es: "Variante baja en carbono de 304 formulada para eliminar la precipitación de carburos durante operaciones de soldadura y sinterizado.",
    de: "Kohlenstoffarme Variante von 304 zur Vermeidung von Karbidausscheidungen beim Schweißen und Sintern.",
    fr: "Variante bas carbone du 304 conçue pour éliminer la précipitation de carbures lors des opérations de soudage et de frittage.",
    pt: "Variante com baixo teor de carbono do 304 formulada para eliminar a precipitação de carbetos na soldagem e sinterização."
  }
}, {
  grade: "AISI 316",
  category: {
    en: "Molybdenum Austenitic",
    zh: "含钼奥氏体不锈钢",
    es: "Austenítico con molibdeno",
    de: "Molybdänhaltiger Austenit",
    fr: "Austénitique au molybdène",
    pt: "Austenítico com molibdênio"
  },
  composition: "16-18% Cr / 10-14% Ni / 2-3% Mo",
  corrosionRating: {
    en: "High (Chloride & Acid Resistance)",
    zh: "高（耐氯化物点蚀与酸性介质）",
    es: "Alta (resistencia a cloruros y ácidos)",
    de: "Hoch (Chlorid- & Säurebeständigkeit)",
    fr: "Élevée (résistance aux chlorures et acides)",
    pt: "Alta (resistência a cloretos e ácidos)"
  },
  costFactor: {
    en: "1.4x",
    zh: "1.4x",
    es: "1.4x",
    de: "1.4x",
    fr: "1.4x",
    pt: "1.4x"
  },
  recommendedUse: {
    en: "Marine straining, chemical processing, fuel filtration, brine filtration",
    zh: "海洋过滤、化工工艺、燃油过滤、盐水及弱酸介质分离",
    es: "Filtración marina, procesamiento químico, filtración de combustibles, salmuera",
    de: "Marinefiltration, chemische Verfahrenstechnik, Kraftstofffiltration, Solefiltration",
    fr: "Filtration marine, procédés chimiques, filtration de carburant, saumure",
    pt: "Filtragem marítima, processos químicos, filtração de combustíveis, salmoura"
  },
  weldability: {
    en: "Good (Suitable for moderate welding)",
    zh: "良好（适用于常规焊接工序）",
    es: "Buena (adecuada para soldadura moderada)",
    de: "Gut (geeignet für mäßiges Schweißen)",
    fr: "Bonne (adaptée au soudage modéré)",
    pt: "Boa (adequada para soldagem moderada)"
  },
  description: {
    en: "Contains 2-3% molybdenum providing significantly increased resistance to chlorides, industrial solvents, and aggressive chemical media.",
    zh: "含有2-3%的钼元素，显著提高抗氯离子点蚀、耐工业溶剂与侵蚀性化学介质能力。",
    es: "Contiene 2-3% de molibdeno, lo que proporciona una resistencia significativamente mayor a cloruros, solventes industriales y medios químicos agresivos.",
    de: "Enthält 2–3 % Molybdän für deutlich höheren Widerstand gegen Chloride, industrielle Lösungsmittel und aggressive Chemikalien.",
    fr: "Contient 2 à 3 % de molybdène pour une résistance nettement accrue aux chlorures, solvants industriels et milieux chimiques agressifs.",
    pt: "Contém 2 a 3% de molibdênio, oferecendo resistência significativamente maior a cloretos, solventes industriais e meios químicos agressivos."
  }
}, {
  grade: "AISI 316L",
  category: {
    en: "Low-Carbon Molybdenum",
    zh: "超低碳含钼奥氏体不锈钢",
    es: "Bajo en carbono con molibdeno",
    de: "Kohlenstoffarmer Molybdän-Austenit",
    fr: "Bas carbone au molybdène",
    pt: "Baixo carbono com molibdênio"
  },
  composition: "16-18% Cr / 10-14% Ni / 2-3% Mo / <0.03% C",
  corrosionRating: {
    en: "Superior (Chloride + Intergranular)",
    zh: "卓越（抗氯化物点蚀与晶间腐蚀）",
    es: "Superior (cloruros + intergranular)",
    de: "Überragend (Chlorid- & interkristalline Beständigkeit)",
    fr: "Supérieure (chlorures + intergranulaire)",
    pt: "Superior (cloretos + intergranular)"
  },
  costFactor: {
    en: "1.45x",
    zh: "1.45x",
    es: "1.45x",
    de: "1.45x",
    fr: "1.45x",
    pt: "1.45x"
  },
  recommendedUse: {
    en: "Pharmaceutical filter discs, sterile liquid filtration, offshore polymer packs",
    zh: "制药滤盘、无菌液体过滤、海上平台聚合物挤出滤网",
    es: "Discos filtrantes farmacéuticos, filtración de líquidos estériles, paquetes de polímeros offshore",
    de: "Pharmazeutische Filterscheiben, sterile Flüssigkeitsfiltration, Offshore-Polymerpakete",
    fr: "Disques filtrants pharmaceutiques, filtration liquide stérile, packs de filtration de polymères",
    pt: "Discos de filtro farmacêuticos, filtração de líquidos estéreis, telas para polímeros offshore"
  },
  weldability: {
    en: "Exceptional (Ideal for sintered laminates)",
    zh: "卓越（多层烧结复合网的首选牌号）",
    es: "Excepcional (ideal para laminados sinterizados)",
    de: "Außergewöhnlich (ideal für Sinterverbunde)",
    fr: "Exceptionnelle (idéale pour les laminés frittés)",
    pt: "Excepcional (ideal para laminados sinterizados)"
  },
  description: {
    en: "The premier specification for critical life sciences, API manufacturing, and heavily welded fine-micron filter elements.",
    zh: "生物制药、原料药生产及精密焊接微米级过滤元件的首选标准牌号。",
    es: "La especificación principal para ciencias biológicas críticas, fabricación de API y elementos filtrantes de micras finas con soldadura intensiva.",
    de: "Die bevorzugte Spezifikation für anspruchsvolle Life-Sciences-Anwendungen, Wirkstoffherstellung und geschweißte Feinfilterelemente.",
    fr: "La nuance de référence pour les sciences du vivant critiques, la fabrication de principes actifs et les éléments filtrants de précision soudés.",
    pt: "A especificação principal para biociências críticas, fabricação de APIs e elementos filtrantes de alta precisão soldados."
  }
}];
interface DecisionRule {
  title: LocalizedField;
  recommendation: LocalizedField;
  reason: LocalizedField;
}
const decisionRules: DecisionRule[] = [{
  title: {
    en: "Chloride Exposure",
    zh: "含氯介质或盐雾工况",
    es: "Exposición a cloruros",
    de: "Chloridbelastung",
    fr: "Exposition aux chlorures",
    pt: "Exposição a cloretos"
  },
  recommendation: {
    en: "Choose 316 or 316L",
    zh: "推荐选用 316 或 316L",
    es: "Elija 316 o 316L",
    de: "316 oder 316L wählen",
    fr: "Choisir 316 ou 316L",
    pt: "Escolha 316 ou 316L"
  },
  reason: {
    en: "Molybdenum content inhibits pitting caused by saline solutions, brackish water, and chlorinated cleaning agents.",
    zh: "钼元素有效抑制盐溶液、微咸水及含氯清洗剂引起的点蚀与缝隙腐蚀。",
    es: "El contenido de molibdeno inhibe la corrosión por picaduras provocada por soluciones salinas, agua salobre y agentes de limpieza clorados.",
    de: "Der Molybdängehalt hemmt Lochfraß durch Salzlösungen, Brackwasser und chlorierte Reinigungsmittel.",
    fr: "Le molybdène empêche la corrosion par piqûres causée par les solutions salines, l'eau saumâtre et les agents de nettoyage chlorés.",
    pt: "O teor de molibdênio inibe a corrosão por pites causada por soluções salinas, água salobra e agentes de limpeza clorados."
  }
}, {
  title: {
    en: "High Welding or Sintering",
    zh: "深度焊接或烧结加工",
    es: "Soldadura intensiva o sinterizado",
    de: "Intensives Schweißen oder Sintern",
    fr: "Soudage intensif ou frittage",
    pt: "Soldagem intensiva ou sinterização"
  },
  recommendation: {
    en: "Choose 304L or 316L",
    zh: "推荐选用 304L 或 316L",
    es: "Elija 304L o 316L",
    de: "304L oder 316L wählen",
    fr: "Choisir 304L ou 316L",
    pt: "Escolha 304L ou 316L"
  },
  reason: {
    en: "Carbon content below 0.03% prevents chromium carbide formation along grain boundaries, protecting joint strength.",
    zh: "碳含量低于0.03%可防止晶界析出碳化铬，确保焊缝与烧结结合处的高耐蚀性与强度。",
    es: "El contenido de carbono inferior al 0.03% evita la formación de carburos de cromo en los límites de grano, protegiendo las uniones.",
    de: "Ein Kohlenstoffgehalt unter 0,03 % verhindert Chromkarbidausscheidungen an den Korngrenzen und schützt Schweißverbindungen.",
    fr: "La teneur en carbone inférieure à 0,03 % évite la formation de carbures de chrome aux joints de grains et préserve la résistance des soudures.",
    pt: "O teor de carbono abaixo de 0,03% previne a formação de carbetos de cromo nos contornos de grão, protegendo a resistência das juntas."
  }
}, {
  title: {
    en: "Moderate Chemical Processing",
    zh: "中度化工与腐蚀性介质",
    es: "Procesamiento químico moderado",
    de: "Mäßige chemische Beanspruchung",
    fr: "Procédés chimiques modérés",
    pt: "Processamento químico moderado"
  },
  recommendation: {
    en: "Choose 316",
    zh: "推荐选用 316",
    es: "Elija 316",
    de: "316 wählen",
    fr: "Choisir 316",
    pt: "Escolha 316"
  },
  reason: {
    en: "Addition of 2-3% molybdenum provides reliable protection against industrial chemicals, organic acids, and mild process streams.",
    zh: "添加2-3%钼元素，对工业化学品、有机酸及温和工艺流体提供可靠防护。",
    es: "La adición de 2-3% de molibdeno brinda protección confiable contra productos químicos industriales, ácidos orgánicos y fluidos moderados.",
    de: "Der Zusatz von 2–3 % Molybdän bietet zuverlässigen Schutz gegen Industriechemikalien, organische Säuren und milde Prozessströme.",
    fr: "L'ajout de 2 à 3 % de molybdène confère une protection fiable contre les produits chimiques industriels, acides organiques et fluides de procédé doux.",
    pt: "A adição de 2 a 3% de molibdênio fornece proteção confiável contra produtos químicos industriais, ácidos orgânicos e correntes de processo amenas."
  }
}, {
  title: {
    en: "Dry Food & Ambient Screening",
    zh: "干燥食品与常温通用筛分",
    es: "Alimentos secos y tamizado ambiente",
    de: "Trockene Lebensmittel & Umgebungssiebung",
    fr: "Aliments secs et criblage ambiant",
    pt: "Alimentos secos e peneiramento ambiente"
  },
  recommendation: {
    en: "Choose 304",
    zh: "推荐选用 304",
    es: "Elija 304",
    de: "304 wählen",
    fr: "Choisir 304",
    pt: "Escolha 304"
  },
  reason: {
    en: "Provides the optimum balance of mechanical rigidity, cleanability, and overall procurement economy.",
    zh: "具备优良的机械刚性、易清洗性以及极高的采购经济性，适合常规工况。",
    es: "Proporciona el equilibrio óptimo entre rigidez mecánica, facilidad de limpieza y economía general de adquisición.",
    de: "Bietet die optimale Balance aus mechanischer Steifigkeit, Reinigungsfähigkeit und wirtschaftlicher Beschaffung.",
    fr: "Offre l'équilibre optimal entre rigidité mécanique, nettoyabilité et rentabilité globale d'achat.",
    pt: "Oferece o equilíbrio ideal entre rigidez mecânica, facilidade de limpeza e economia na aquisição."
  }
}];
const copy = {
  eyebrow: {
    en: "Engineering Materials Guide",
    zh: "工程材料指南",
    es: "Guía de materiales de ingeniería",
    de: "Werkstoff-Leitfaden",
    fr: "Guide des matériaux d'ingénierie",
    pt: "Guia de materiais de engenharia"
  },
  heading: {
    en: "Stainless steel alloy selection guide",
    zh: "不锈钢合金牌号选择指南",
    es: "Guía de selección de aleaciones de acero inoxidable",
    de: "Leitfaden zur Auswahl von Edelstahllegierungen",
    fr: "Guide de sélection des nuances d'acier inoxydable",
    pt: "Guia de seleção de ligas de aço inoxidável"
  },
  lead: {
    en: "Compare operating limits, corrosion resistance, and cost factors across wire mesh grades to specify the right alloy for your filtration process.",
    zh: "对比各不锈钢丝网牌号的运行极限、耐腐蚀性能与成本系数，为您的过滤工艺精准选型。",
    es: "Compare límites operativos, resistencia a la corrosión y factores de coste entre grados de malla para especificar la aleación idónea.",
    de: "Vergleichen Sie Einsatzgrenzen, Korrosionsbeständigkeit und Kostenfaktoren verschiedener Drahtgewebegüten für Ihre Filtration.",
    fr: "Comparez les limites opérationnelles, la résistance à la corrosion et les facteurs de coût pour choisir la nuance adaptée à votre procédé.",
    pt: "Compare limites operacionais, resistência à corrosão e custos entre os graus de tela para definir a liga ideal para seu processo."
  },
  nominalChemistry: {
    en: "Nominal Chemistry: ",
    zh: "标称化学成分：",
    es: "Composición química nominal: ",
    de: "Nominale chemische Zusammensetzung: ",
    fr: "Composition chimique nominale : ",
    pt: "Composição química nominal: "
  },
  viewFullMetallurgy: {
    en: "View full metallurgy",
    zh: "查看完整冶金参数",
    es: "Ver metalurgia completa",
    de: "Vollständige Metallurgie ansehen",
    fr: "Voir la métallurgie complète",
    pt: "Ver metalurgia completa"
  },
  corrosionResistance: {
    en: "Corrosion Resistance",
    zh: "耐腐蚀性能",
    es: "Resistencia a la corrosión",
    de: "Korrosionsbeständigkeit",
    fr: "Résistance à la corrosion",
    pt: "Resistência à corrosão"
  },
  relativeCostIndex: {
    en: "Relative Cost Index",
    zh: "相对成本系数",
    es: "Índice de coste relativo",
    de: "Relativer Kostenfaktor",
    fr: "Indice de coût relatif",
    pt: "Índice de custo relativo"
  },
  fabricationSintering: {
    en: "Fabrication / Sintering",
    zh: "加工与烧结性能",
    es: "Fabricación / Sinterizado",
    de: "Verarbeitung / Sintern",
    fr: "Façonnage / Frittage",
    pt: "Fabricação / Sinterização"
  },
  typicalEnvironments: {
    en: "Typical Process Environments: ",
    zh: "典型应用工艺环境： ",
    es: "Entornos típicos de proceso: ",
    de: "Typische Prozessumgebungen: ",
    fr: "Environnements types de procédé : ",
    pt: "Ambientes típicos de processo: "
  },
  matrixTitle: {
    en: "Side-by-side alloy matrix",
    zh: "不锈钢合金牌号参数对比表",
    es: "Matriz comparativa de aleaciones",
    de: "Gegenüberstellung der Legierungen",
    fr: "Matrice comparative des nuances",
    pt: "Matriz comparativa de ligas"
  },
  matrixSubtitle: {
    en: "Technical parameters for standard woven wire cloth and sintered filter media.",
    zh: "标准编织丝网与烧结过滤介质的技术规格参数。",
    es: "Parámetros técnicos para telas metálicas estándar y medios filtrantes sinterizados.",
    de: "Technische Parameter für Standard-Drahtgewebe und gesinterte Filtermedien.",
    fr: "Paramètres techniques pour toiles métalliques standard et médias filtrants frittés.",
    pt: "Parâmetros técnicos para telas tecidas padrão e meios filtrantes sinterizados."
  },
  allMaterialsLink: {
    en: "All materials & specs",
    zh: "全部材料与规格",
    es: "Todos los materiales y especificaciones",
    de: "Alle Werkstoffe & Daten",
    fr: "Tous les matériaux et caractéristiques",
    pt: "Todos os materiais e especificações"
  },
  thAlloyGrade: {
    en: "Alloy Grade",
    zh: "合金牌号",
    es: "Grado de aleación",
    de: "Legierungsgüte",
    fr: "Nuance d'alliage",
    pt: "Grau de liga"
  },
  thNominalChemistry: {
    en: "Nominal Chemistry",
    zh: "标称成分",
    es: "Composición nominal",
    de: "Nominale Zusammensetzung",
    fr: "Composition nominale",
    pt: "Composição nominal"
  },
  thCorrosionResistance: {
    en: "Corrosion Resistance",
    zh: "耐腐蚀性能",
    es: "Resistencia a la corrosión",
    de: "Korrosionsbeständigkeit",
    fr: "Résistance à la corrosion",
    pt: "Resistência à corrosão"
  },
  thCostIndex: {
    en: "Cost Index",
    zh: "成本系数",
    es: "Índice de coste",
    de: "Kostenindex",
    fr: "Indice de coût",
    pt: "Índice de custo"
  },
  thPrimaryApplications: {
    en: "Primary Applications",
    zh: "主要应用领域",
    es: "Aplicaciones principales",
    de: "Hauptanwendungen",
    fr: "Applications principales",
    pt: "Aplicações principais"
  },
  standardChoice: {
    en: "Standard",
    zh: "标准选用",
    es: "Estándar",
    de: "Standard",
    fr: "Standard",
    pt: "Padrão"
  },
  criteriaTitle: {
    en: "Selection decision criteria",
    zh: "选型决策准则",
    es: "Criterios de decisión para la selección",
    de: "Entscheidungskriterien zur Auswahl",
    fr: "Critères de décision pour le choix",
    pt: "Critérios de decisão para a seleção"
  },
  criteriaSubtitle: {
    en: "Core rules for matching stainless alloys to plant conditions and mechanical requirements.",
    zh: "根据工况介质条件与机械要求匹配合适不锈钢合金的核心规则。",
    es: "Reglas fundamentales para adaptar aleaciones inoxidables a las condiciones de planta y requisitos mecánicos.",
    de: "Wesentliche Regeln zur Abstimmung von Edelstahllegierungen auf Betriebsbedingungen und mechanische Anforderungen.",
    fr: "Règles essentielles pour faire correspondre les alliages inoxydables aux conditions d'exploitation et contraintes mécaniques.",
    pt: "Regras fundamentais para combinar ligas inoxidáveis com condições operacionais e exigências mecânicas."
  },
  customValidationTitle: {
    en: "Need custom alloy validation?",
    zh: "需要定制合金材质验证？",
    es: "¿Necesita validación personalizada de aleaciones?",
    de: "Benötigen Sie eine spezifische Werkstoffvalidierung?",
    fr: "Besoin d'une validation d'alliage sur mesure ?",
    pt: "Precisa de validação personalizada da liga?"
  },
  customValidationSubtitle: {
    en: "Positive material identification (PMI) and chemical composition reports available when specified in the order.",
    zh: "可按订单要求提供材料光谱分析（PMI）检测与材质成分报告。",
    es: "Identificación positiva de materiales (PMI) e informes de composición química disponibles según especificación de pedido.",
    de: "Positive Materialidentifikation (PMI) und chemische Analyseberichte auf Anfrage auftragsbezogen erhältlich.",
    fr: "Contrôle PMI et rapports de composition chimique disponibles sur demande selon la commande.",
    pt: "Identificação positiva de materiais (PMI) e relatórios de composição química disponíveis quando solicitados no pedido."
  },
  viewAllGrades: {
    en: "View all grades",
    zh: "查看所有牌号",
    es: "Ver todos los grados",
    de: "Alle Güten ansehen",
    fr: "Voir toutes les nuances",
    pt: "Ver todos os graus"
  },
  consultEngineer: {
    en: "Consult an engineer",
    zh: "咨询材料工程师",
    es: "Consultar a un ingeniero",
    de: "Ingenieur konsultieren",
    fr: "Consulter un ingénieur",
    pt: "Consultar engenheiro"
  }
};
export default function AlloySelection() {
  const {
    lang
  } = useLanguage();
  const [selectedGrade, setSelectedGrade] = useState<string>("AISI 316L");
  const activeAlloy = alloyData.find(a => a.grade === selectedGrade) || alloyData[3];
  return <section id="alloyselectionguide" data-nav="light" className="bg-background text-foreground mf-section border-b border-border transition-colors">
      <div className="mf-container">
        {/* Header Block */}
        <Reveal className="max-w-3xl mb-14">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="mf-caption uppercase tracking-wider font-semibold border-primary/30 text-primary">
              {pick(lang, copy.eyebrow)}
            </Badge>
          </div>
          <h2 className="mf-h2 font-semibold text-foreground">
            {pick(lang, copy.heading)}
          </h2>
          <p className="mt-4 mf-lead text-muted-foreground">
            {pick(lang, copy.lead)}
          </p>
        </Reveal>

        {/* Quick Switcher & Active Grade Detail Card */}
        <Reveal delay={100} className="mb-16">
          <div className="flex flex-wrap gap-2 mb-6 p-1.5 bg-muted/60 rounded-lg border border-border w-fit">
            {alloyData.map((item, i) => <Button key={item.grade} data-index={i} variant={selectedGrade === item.grade ? "default" : "ghost"} size="sm" onClick={() => setSelectedGrade(item.grade)} className="font-medium mf-caption sm:text-sm px-4 py-2 transition-colors">
                {item.grade}
              </Button>)}
          </div>

          <Card className="border border-border bg-card rounded-lg overflow-hidden">
            <CardHeader className="bg-muted/30 border-b border-border pb-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <CardTitle className="mf-h3 font-bold text-foreground">
                      {activeAlloy.grade}
                    </CardTitle>
                    <Badge variant="outline" className="bg-muted text-foreground font-mono mf-caption border-border">
                      {pick(lang, activeAlloy.category)}
                    </Badge>
                  </div>
                  <CardDescription className="text-sm mt-1 text-muted-foreground">
                    {pick(lang, copy.nominalChemistry)}{activeAlloy.composition}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Button asChild variant="outline" size="sm">
                    <Link to="/MaterialsAndGradesPage" className="inline-flex items-center gap-1.5">
                      <FileText className="size-4" aria-hidden="true" />
                      <span>{pick(lang, copy.viewFullMetallurgy)}</span>
                    </Link>
                  </Button>
                  <Button asChild size="sm">
                    <Link to="/RequestAQuotePage" className="inline-flex items-center gap-1.5">
                      <span>{pick(lang, COMMON.requestQuote)}</span>
                      <ArrowRight className="size-4" aria-hidden="true" />
                    </Link>
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-6">
              <p className="text-foreground text-sm sm:text-base leading-relaxed mb-6 font-default">
                {pick(lang, activeAlloy.description)}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-background border border-border">
                  <div className="flex items-center gap-2 text-primary mf-caption font-semibold uppercase tracking-wider mb-2">
                    <ShieldAlert className="size-4" aria-hidden="true" />
                    <span>{pick(lang, copy.corrosionResistance)}</span>
                  </div>
                  <div className="text-sm font-medium text-foreground">
                    {pick(lang, activeAlloy.corrosionRating)}
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-background border border-border">
                  <div className="flex items-center gap-2 text-primary mf-caption font-semibold uppercase tracking-wider mb-2">
                    <DollarSign className="size-4" aria-hidden="true" />
                    <span>{pick(lang, copy.relativeCostIndex)}</span>
                  </div>
                  <div className="text-sm font-medium text-foreground">
                    {pick(lang, activeAlloy.costFactor)}
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-background border border-border">
                  <div className="flex items-center gap-2 text-primary mf-caption font-semibold uppercase tracking-wider mb-2">
                    <Layers className="size-4" aria-hidden="true" />
                    <span>{pick(lang, copy.fabricationSintering)}</span>
                  </div>
                  <div className="text-sm font-medium text-foreground">
                    {pick(lang, activeAlloy.weldability)}
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 rounded-lg bg-muted/40 border border-border/80 flex flex-col md:flex-row md:items-center justify-between gap-3 text-sm">
                <div>
                  <span className="font-semibold text-foreground">
                    {pick(lang, copy.typicalEnvironments)}
                  </span>
                  <span className="text-muted-foreground">
                    {pick(lang, activeAlloy.recommendedUse)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </Reveal>

        {/* Master Comparison Table */}
        <Reveal delay={150} className="mb-16">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <h3 className="mf-h3 font-semibold text-foreground">
                {pick(lang, copy.matrixTitle)}
              </h3>
              <p className="mf-caption text-muted-foreground">
                {pick(lang, copy.matrixSubtitle)}
              </p>
            </div>
            <Link to="/MaterialsAndGradesPage" className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1 shrink-0">
              <span>{pick(lang, copy.allMaterialsLink)}</span>
              <ArrowRight className="size-3.5" aria-hidden="true" />
            </Link>
          </div>

          <div className="rounded-lg border border-border bg-card overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow>
                  <TableHead className="w-[140px] font-semibold text-foreground">
                    {pick(lang, copy.thAlloyGrade)}
                  </TableHead>
                  <TableHead className="w-[160px] font-semibold text-foreground">
                    {pick(lang, copy.thNominalChemistry)}
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    {pick(lang, copy.thCorrosionResistance)}
                  </TableHead>
                  <TableHead className="w-[110px] font-semibold text-foreground">
                    {pick(lang, copy.thCostIndex)}
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    {pick(lang, copy.thPrimaryApplications)}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alloyData.map((item, i) => <TableRow key={item.grade} data-index={i} className={selectedGrade === item.grade ? "bg-accent/40" : undefined}>
                    <TableCell className="font-semibold text-foreground">
                      <div className="flex items-center gap-2">
                        <span>{item.grade}</span>
                        {item.grade === "AISI 316L" && <Badge variant="outline" className="mf-caption py-0 px-1 border-primary/40 text-primary">
                            {pick(lang, copy.standardChoice)}
                          </Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono mf-caption text-muted-foreground">{item.composition}</TableCell>
                    <TableCell className="mf-caption sm:text-sm text-foreground">
                      {pick(lang, item.corrosionRating)}
                    </TableCell>
                    <TableCell className="font-mono mf-caption text-foreground font-medium">
                      {pick(lang, item.costFactor)}
                    </TableCell>
                    <TableCell className="mf-caption sm:text-sm text-muted-foreground">
                      {pick(lang, item.recommendedUse)}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>
        </Reveal>

        {/* Engineering Decision Rules Grid */}
        <Reveal delay={200}>
          <div className="mb-6">
            <h3 className="mf-h3 font-semibold text-foreground">
              {pick(lang, copy.criteriaTitle)}
            </h3>
            <p className="mf-caption text-muted-foreground">
              {pick(lang, copy.criteriaSubtitle)}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {decisionRules.map((rule, i) => <div key={rule.title.en} data-index={i} className="p-5 rounded-lg border border-border bg-card flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2 text-primary">
                    <CircleCheck className="size-4 shrink-0 text-primary" aria-hidden="true" />
                    <span className="text-sm font-semibold text-foreground">
                      {pick(lang, rule.title)}
                    </span>
                  </div>
                  <div className="inline-block px-2 py-0.5 rounded mf-caption font-semibold bg-muted text-foreground border border-border mb-3">
                    {pick(lang, rule.recommendation)}
                  </div>
                  <p className="mf-caption text-muted-foreground leading-relaxed font-default">
                    {pick(lang, rule.reason)}
                  </p>
                </div>
              </div>)}
          </div>

          {/* Consultation Strip */}
          <div className="mt-10 p-6 rounded-lg border border-border bg-muted/40 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                <CircleHelp className="size-5" aria-hidden="true" />
              </div>
              <div>
                <h4 className="text-base font-semibold text-foreground">
                  {pick(lang, copy.customValidationTitle)}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {pick(lang, copy.customValidationSubtitle)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0 w-full sm:w-auto">
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/MaterialsAndGradesPage">
                  <span>{pick(lang, copy.viewAllGrades)}</span>
                </Link>
              </Button>
              <Button asChild className="w-full sm:w-auto">
                <Link to="/RequestAQuotePage">
                  <span>{pick(lang, copy.consultEngineer)}</span>
                </Link>
              </Button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}